export function buildMoveproContent({ lead, dayjs }) {
    if (!lead) return '';

    const sections = [];
    const M3 = 'm³';

    const pushSection = (title, lines) => {
        if (!lines.length) return;
        sections.push(title);
        sections.push(...lines);
        sections.push(''); // blank line between sections
    };

    const formatYYYYMMDD = (value) => {
        if (!value) return null;
        const d = dayjs(value);
        if (!d.isValid()) return null;
        return d.format('YYYY-MM-DD');
    };

    const moveTypeLabel = (leadObj) => {
        const raw = leadObj?.move?.type ?? leadObj?.move_type ?? null;
        if (!raw) return null;
        const up = String(raw).toUpperCase();
        if (up === 'LOCAL' || up === 'INTERSTATE') return up;
        return null;
    };

    const numericValue = (val) => {
        if (val == null) return null;
        const n = Number(val);
        return Number.isFinite(n) ? n : null;
    };

    const addressString = (addr) => {
        if (!addr) return null;
        if (typeof addr === 'string') return addr.trim() || null;
        // AddressShortResource uses "address", raw models use "formatted"
        const raw = addr.formatted ?? addr.address ?? null;
        if (raw) return String(raw).trim() || null;
        const parts = [addr.street_address, addr.locality, addr.state, addr.postal_code].filter(Boolean);
        return parts.length ? parts.join(' ').trim() : null;
    };

    const pickupAddress = (leadObj) => {
        return (
            addressString(leadObj?.move?.route?.start) ??
            addressString(leadObj?.move?.pickup_address_formatted) ??
            addressString(leadObj?.pickup_address) ??
            addressString(leadObj?.pickup_location) ??
            null
        );
    };

    const deliveryAddress = (leadObj) => {
        return (
            addressString(leadObj?.move?.route?.end) ??
            addressString(leadObj?.move?.delivery_address_formatted) ??
            addressString(leadObj?.delivery_address) ??
            addressString(leadObj?.delivery_location) ??
            null
        );
    };

    const normalizeDwellingType = (value) => {
        if (!value) return null;
        const up = String(value).trim().toUpperCase();
        const map = {
            HOUSE: 'HOUSE',
            APARTMENT: 'APARTMENT',
            UNIT: 'APARTMENT',
            FLAT: 'APARTMENT',
            TOWNHOUSE: 'TOWNHOUSE',
            STORAGE: 'STORAGE',
            OFFICE: 'OFFICE',
        };
        return map[up] || null;
    };

    const pickupDwellingType = (leadObj) => {
        return (
            normalizeDwellingType(leadObj?.move?.route?.start?.dwelling_type) ??
            normalizeDwellingType(leadObj?.move?.pickup_dwelling) ??
            normalizeDwellingType(leadObj?.pickup_dwelling) ??
            null
        );
    };

    const deliveryDwellingType = (leadObj) => {
        return (
            normalizeDwellingType(leadObj?.move?.route?.end?.dwelling_type) ??
            normalizeDwellingType(leadObj?.move?.delivery_dwelling) ??
            normalizeDwellingType(leadObj?.delivery_dwelling) ??
            null
        );
    };

    const pickupBedrooms = (leadObj) => {
        return (
            numericValue(leadObj?.move?.route?.start?.bedrooms) ??
            numericValue(leadObj?.move?.pickup_bedrooms) ??
            numericValue(leadObj?.pickup_bedrooms) ??
            null
        );
    };

    const deliveryBedrooms = (leadObj) => {
        return (
            numericValue(leadObj?.move?.route?.end?.bedrooms) ??
            numericValue(leadObj?.move?.delivery_bedrooms) ??
            numericValue(leadObj?.delivery_bedrooms) ??
            null
        );
    };

    const pickupStorageAreaM2 = (leadObj) => {
        return (
            numericValue(leadObj?.move?.route?.start?.storage_area_m2) ??
            numericValue(leadObj?.move?.pickup_storage_area_m2) ??
            numericValue(leadObj?.pickup_storage_area_m2) ??
            null
        );
    };

    const deliveryStorageAreaM2 = (leadObj) => {
        return (
            numericValue(leadObj?.move?.route?.end?.storage_area_m2) ??
            numericValue(leadObj?.move?.delivery_storage_area_m2) ??
            numericValue(leadObj?.delivery_storage_area_m2) ??
            null
        );
    };

    const totalSpaceM3 = (leadObj) => {
        return numericValue(
            leadObj?.move?.space ?? leadObj?.move?.total_space_m3 ?? leadObj?.move?.volume ?? leadObj?.volume ?? null,
        );
    };

    const coerceInventoryQty = (item) => {
        const q = item?.qty ?? item?.quantity ?? item?.count ?? null;
        const n = Number(q);
        if (!Number.isFinite(n)) return null;
        const i = Math.round(n);
        return i > 0 ? i : null;
    };

    const inventoryItemName = (item) => {
        const n = item?.name ?? item?.label ?? item?.inventory_option?.name ?? item?.inventoryOption?.name ?? null;
        return n ? String(n).trim() : null;
    };

    const inventoryItemVolume = (item, qty) => {
        // Treat volume fields as "per 1 item" (unit m³). We output total m³ for the line item.
        const unit =
            item?.volume_m3 ??
            item?.volume ??
            item?.cubic_meters ??
            item?.m3 ??
            // API shape (muval-api MoveInventoryResource)
            item?.inventory_option?.volume ??
            item?.inventoryOption?.volume ??
            null;

        const unitM3 = numericValue(unit);
        if (unitM3 == null) return null;

        const q = Number(qty);
        const total = Number.isFinite(q) && q > 0 ? unitM3 * q : unitM3;
        return String(Number(total.toFixed(2)));
    };

    const inventoryCategoryName = (item) => {
        const c =
            item?.inventory_option?.category?.name ??
            item?.inventoryOption?.category?.name ??
            item?.inventory_option?.category_name ??
            item?.inventoryOption?.categoryName ??
            null;
        return c ? String(c).trim() : null;
    };

    // LEAD
    const leadLines = [];
    if (lead?.source) leadLines.push(`Source: ${lead.source}`);
    const created = formatYYYYMMDD(lead?.created_at);
    if (created) leadLines.push(`Created: ${created}`);
    pushSection('LEAD', leadLines);

    // CUSTOMER
    const customerLines = [];
    if (lead?.customer?.first_name) customerLines.push(`First name: ${lead.customer.first_name}`);
    if (lead?.customer?.last_name) customerLines.push(`Last name: ${lead.customer.last_name}`);
    if (lead?.customer?.phone) customerLines.push(`Phone: ${lead.customer.phone}`);
    if (lead?.customer?.email) customerLines.push(`Email: ${lead.customer.email}`);
    pushSection('CUSTOMER', customerLines);

    // MOVE
    const moveLines = [];
    const moveType = moveTypeLabel(lead);
    if (moveType) moveLines.push(`Move type: ${moveType}`);
    const pAddr = pickupAddress(lead);
    if (pAddr) moveLines.push(`Pickup address: ${pAddr}`);
    const dAddr = deliveryAddress(lead);
    if (dAddr) moveLines.push(`Delivery address: ${dAddr}`);

    const pDwelling = pickupDwellingType(lead);
    const dDwelling = deliveryDwellingType(lead);
    if (pDwelling) moveLines.push(`Pickup dwelling: ${pDwelling}`);
    if (dDwelling) moveLines.push(`Delivery dwelling: ${dDwelling}`);

    const pBeds = pickupBedrooms(lead);
    const dBeds = deliveryBedrooms(lead);
    if (pBeds != null) moveLines.push(`Pickup bedrooms: ${pBeds}`);
    if (dBeds != null) moveLines.push(`Delivery bedrooms: ${dBeds}`);

    const pStorage = pickupStorageAreaM2(lead);
    const dStorage = deliveryStorageAreaM2(lead);
    if (pDwelling === 'STORAGE' && pStorage != null) moveLines.push(`Pickup storage area (m²): ${pStorage}`);
    if (dDwelling === 'STORAGE' && dStorage != null) moveLines.push(`Delivery storage area (m²): ${dStorage}`);

    pushSection('MOVE', moveLines);

    // PICKUP DATES
    const pickupDateLines = [];
    const from = formatYYYYMMDD(lead?.move?.pickup_date_from);
    const by = formatYYYYMMDD(lead?.move?.pickup_date_by);
    if (from && by) {
        if (from === by) pickupDateLines.push(`Pickup date (YYYY-MM-DD): ${from}`);
        else pickupDateLines.push(`Pickup date range (YYYY-MM-DD to YYYY-MM-DD): ${from} to ${by}`);
    } else if (from) {
        pickupDateLines.push(`Pickup date (YYYY-MM-DD): ${from}`);
    } else if (by) {
        pickupDateLines.push(`Pickup date (YYYY-MM-DD): ${by}`);
    }
    pushSection('PICKUP DATES', pickupDateLines);

    // PICKUP ACCESS
    const formatAccessLines = (accessItems) => {
        if (!Array.isArray(accessItems) || !accessItems.length) return [];
        const lines = [];
        for (const item of accessItems) {
            const tag = item?.accessTag ?? item?.access_tag ?? null;
            const label = tag?.label ?? tag?.item ?? null;
            if (!label) continue;
            const val = item?.value ?? null;
            const unit = tag?.unit ?? null;
            if (val != null && String(val).trim()) {
                const valStr = unit ? `${val} ${unit}` : String(val);
                lines.push(`- ${label}: ${valStr}`);
            } else {
                lines.push(`- ${label}`);
            }
        }
        return lines;
    };

    const pickupAccessItems = lead?.move?.pickup_access ?? lead?.move?.pickupAccess ?? [];
    pushSection('PICKUP ACCESS', formatAccessLines(pickupAccessItems));

    const deliveryAccessItems = lead?.move?.delivery_access ?? lead?.move?.deliveryAccess ?? [];
    pushSection('DELIVERY ACCESS', formatAccessLines(deliveryAccessItems));

    // SPECIAL INSTRUCTIONS (verbatim)
    const instructions =
        lead?.special_instructions ??
        lead?.notes ??
        lead?.move?.special_instructions ??
        lead?.move?.customer_notes ??
        null;
    const instrLines = [];
    if (instructions) {
        const rawLines = String(instructions)
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean);
        for (const l of rawLines) instrLines.push(`- ${l}`);
    }
    pushSection('SPECIAL INSTRUCTIONS', instrLines);

    // TOTAL SPACE
    const spaceLines = [];
    const space = totalSpaceM3(lead);
    if (space != null) spaceLines.push(`Total space: ${space}${M3}`);
    pushSection('TOTAL SPACE', spaceLines);

    // INVENTORY (optional). Prefer currentInventory.items, but only when it actually has
    // items — an empty array must not shadow a legacy move.inventory list (which
    // MoveproImportModals also treats as valid inventory).
    const inv = [];
    const currentInventoryItems =
        Array.isArray(lead?.move?.currentInventory?.items) ? lead.move.currentInventory.items : [];
    const legacyInventoryItems = Array.isArray(lead?.move?.inventory) ? lead.move.inventory : [];
    const items = currentInventoryItems.length ? currentInventoryItems : legacyInventoryItems;

    const categoryOrder = [];
    const byCategory = new Map();
    for (const it of items) {
        const name = inventoryItemName(it);
        const qty = coerceInventoryQty(it);
        if (!name || qty == null) continue;
        const vol = inventoryItemVolume(it, qty);
        const line = vol ? `${qty} x ${name} (${vol}${M3})` : `${qty} x ${name}`;

        const category = inventoryCategoryName(it) || 'Uncategorised';
        if (!byCategory.has(category)) {
            byCategory.set(category, []);
            categoryOrder.push(category);
        }
        byCategory.get(category).push(line);
    }

    if (categoryOrder.length) {
        const sorted = [...categoryOrder].sort((a, b) => a.localeCompare(b));
        for (const category of sorted) {
            inv.push(`[${category}]`);
            const lines = byCategory.get(category) || [];
            // keep stable ordering within category
            inv.push(...lines.sort((a, b) => a.localeCompare(b)));
        }
    }
    pushSection('INVENTORY', inv);

    let out = sections.join('\n').trimEnd();
    if (out.length > 10000) out = out.slice(0, 10000);
    return out;
}
